import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { first, forkJoin, map } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MutationQuery } from 'src/app/models/shared/enums/mutation-query.enum';
import { UtilitiesService } from 'src/app/shared/services/utilities.service';
import { TemplateShotTypeDTI, UpdateTemplate } from 'src/app/components/company/models/update-template.type';
import { Ids } from 'src/app/models/common/ids.model';
import { ShotCombination } from 'src/app/models/common/types/shot-combination.type';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { AppService } from 'src/app/app.service';
import { Brand } from 'src/app/models/common/brand.model';
import { Category } from 'src/app/models/common/category.model';
import { Style } from 'src/app/models/common/style.model';
import { Color } from 'src/app/models/common/color.model';
import { ShotType } from 'src/app/models/common/shot-type.model';
import { Template, TemplateShotType } from 'src/app/components/company/models/template.type';
import { PageInfo } from 'src/app/models/shared/types/page-info.type';
import { SizeGroup } from 'src/app/models/common/size-group.model';
import { MediaFormat } from 'src/app/models/common/media-format.type';
import { AspectRatio } from 'src/app/models/common/aspect-ratio.type';
import { CodifiedUsed } from 'src/app/models/common/codified-used.type';
import { SharedQueries, TemplatesQueries } from 'src/app/models/shared/enums/get-query';
import { WrapperResultGraphQL } from 'src/app/models/shared/types/wrapper-result-graphql.type';
import { UtilityFunctions } from 'src/app/shared/utilities/functions.models';

@Component({
	selector: 'bt-template-form',
	standalone: false,
	templateUrl: './template-form.component.html',
	styleUrl: './template-form.component.scss'
})
export class TemplateFormComponent {
	readonly #destroyRef = inject(DestroyRef);
	readonly #graphQLService = inject(GraphQLService);
	readonly #appService = inject(AppService);
	readonly #activatedRoute = inject(ActivatedRoute);
	readonly #router = inject(Router);
	readonly #utilitiesService = inject(UtilitiesService);
	editMode: boolean = false;

	brand: Brand | undefined;
	category: Category | undefined;
	stylesSelected: Array<Style> = [];
	colorsSelected: Array<Color> = [];
	shotTypesSelected: Array<ShotType> = [];
	shotCombinations: Array<ShotCombination> = [];
	shoots: Array<TemplateShotType> = [];
	template: Template | undefined;
	differentShotsForSizeGroups: boolean = false;

	//Selected Data
	brands: { data: Array<Brand>; pageInfo: PageInfo } = {
		data: [],
		pageInfo: {
			hasNextPage: false,
			hasPreviusPage: false,
			startCursor: null,
			endCursor: null
		}
	};
	categories: { data: Array<Category>; pageInfo: PageInfo } = {
		data: [],
		pageInfo: {
			hasNextPage: false,
			hasPreviusPage: false,
			startCursor: null,
			endCursor: null
		}
	};
	styles: { data: Array<Style>; page: number; search: string; paginationSize: number } = {
		data: [],
		page: 0,
		paginationSize: 50,
		search: ''
	};
	colors: { data: Array<Color>; page: number; search: string; paginationSize: number; pageInfo: PageInfo } = {
		data: [],
		page: 0,
		paginationSize: 50,
		search: '',
		pageInfo: {
			startCursor: null,
			endCursor: null,
			hasNextPage: false,
			hasPreviusPage: false
		}
	};

	//All Data
	shotTypes: { data: Array<ShotType>; size: number; search: string; pageInfo: PageInfo } = {
		data: [],
		size: 50,
		search: '',
		pageInfo: {
			startCursor: null,
			endCursor: null,
			hasNextPage: false,
			hasPreviusPage: false
		}
	};
	sizeGroups: { data: Array<SizeGroup>; selected?: SizeGroup; disabledDeleteAction?: boolean } = {
		data: []
	};
	mediaTypes: { data: Array<MediaFormat>; size: number; search: string } = {
		data: [],
		size: 50,
		search: ''
	};
	aspectRatios: { data: Array<AspectRatio>; size: number; search: string } = {
		data: [],
		size: 50,
		search: ''
	};
	codifiedUseds: { data: Array<CodifiedUsed>; size: number; search: string; pageInfo: PageInfo } = {
		data: [],
		size: 50,
		search: '',
		pageInfo: {
			startCursor: null,
			endCursor: null,
			hasNextPage: false,
			hasPreviusPage: false
		}
	};

	constructor() {
		this.#activatedRoute.snapshot.queryParams['templateId'] ? (this.editMode = true) : (this.editMode = false);
		if (this.editMode) {
			this.getTemplate();
		} else {
			this.getBrands();
			this.loadInitialData();
		}
	}

	addShot() {
		this.shoots.push(
			{
				shotTypeId: undefined,
				shotTypeCode: undefined,
				shotTypeName: undefined,
				sizeGroupId: undefined,
				sizeGroupName: undefined,
				sizeIndex: 0,
				mediaFormatId: undefined,
				aspectRatioId: undefined,
				codifiedUsedId: undefined,
				codifiedUsedCode: undefined,
				codifiedUsedName: undefined
			} as TemplateShotType
		);
	}

	removeShot(index: number) {
		this.shoots.splice(index, 1);
	}

	onDifferentShotsForSizeGroups() {
		if (this.differentShotsForSizeGroups) {
			this.shoots = [];
			this.getSizeGroups(this.brand?.brandId!);
		} else {
			this.sizeGroups.data = [];
			this.sizeGroups.selected = undefined;
			this.shoots = [{ sizeGroupId: undefined, sizeGroupName: undefined, shotTypeId: undefined, shotTypeName: undefined } as TemplateShotType];
		}
	}

	getCategories(event: any) {
		const brand = event.value;
		this.category = undefined;
		this.#graphQLService
			.getData<Category>(SharedQueries.GET_CATEGORIES_NO_IDS, { brandIds: [brand.brandId] })
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => res['data']['category'])
			)
			.subscribe({
				next: (data: WrapperResultGraphQL<Category>) => {
					!!this.categories.pageInfo.endCursor
						? (this.categories.data = UtilityFunctions.DeepClone(data.nodes))
						: (this.categories.data = this.categories.data.concat(UtilityFunctions.DeepClone(data.nodes)));

					this.categories.pageInfo = { ...data.pageInfo };

					if (!!this.template?.categoryId) {
						this.category = this.categories.data.find((el) => el.id === this.template.categoryId);
						this.getCategoryStyles(0);
					}
				}
			});
	}

	getCategoryStyles(page: number) {
		if (!this.brand || !this.category) {
			console.error('Brand or category not selected');
			return;
		}

		this.styles.page = page;
		const filter = {
			brandId: this.brand.brandId,
			categoryId: this.category.id,
			paginationPage: page,
			paginationSize: this.styles.paginationSize,
			search: this.styles.search,
			companyId: this.#appService.companyId
		};

		this.#graphQLService
			.getData<Style>(TemplatesQueries.GET_CATEGORY_STYLE, filter)
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => res['data']['categoryStyle'])
			)
			.subscribe({
				next: (data: WrapperResultGraphQL<Style>) => {
					!this.styles.page
						? (this.styles.data = UtilityFunctions.DeepClone(data.nodes))
						: (this.styles.data = this.styles.data.concat(UtilityFunctions.DeepClone(data.nodes)));

					if (!!this.template?.styles.length) {
						this.stylesSelected = this.styles.data.filter((el) => this.template.styles.some((t) => t.modelId === el.modelId));
						this.getColorsByModel();
					}
				}
			});
	}

	getColorsByModel() {
		const modelId = this.stylesSelected[0].modelId;
		const filter = {
			first: this.colors.paginationSize,
			after: this.colors.pageInfo.endCursor,
			modelId,
			search: this.colors.search
		};
		this.#graphQLService
			.getData<Color>(SharedQueries.GET_COLORS_BY_MODEL, filter)
			.pipe(
				map((res: any) => res['data']['colors']),
				takeUntilDestroyed(this.#destroyRef)
			)
			.subscribe({
				next: (data: WrapperResultGraphQL<any>) => {
					!this.colors.pageInfo.endCursor
						? (this.colors.data = data.nodes.map((el) => ({
								colorId: el.id,
								colorCode: el.code,
								colorName: el.finalColorName,
								fullName: `${el.code}${el.finalColorName.trim() ? ' - ' + el.finalColorName.trim() : ''}`
						  })))
						: (this.colors.data = this.colors.data.concat(
								data.nodes.map((el) => ({
									colorId: el.id,
									colorCode: el.code,
									colorName: el.finalColorName,
									fullName: `${el.code}${el.finalColorName.trim() ? ' - ' + el.finalColorName.trim() : ''}`
								}))
						  ));
					this.colors.pageInfo = { ...data.pageInfo };

					if (!!this.template?.colors.length) {
						this.colorsSelected = this.colors.data.filter((el) => this.template.colors.some((t) => t.colorId === el.colorId));
					}
				}
			});
	}

	isShotTypeSelected(id: number, index: number): boolean {
		if (!this.differentShotsForSizeGroups) {
			return this.shoots.some((el) => el.shotTypeId === id);
		}

		const type = this.shoots[index];
		if (!!type) return this.shoots.some((el) => el.shotTypeId === id && el.sizeGroupId === type?.sizeGroupId);

		return false;
	}

	selectShotType(event: any, index: number) {
		const shotType = this.shotTypes.data.find((el) => el.id === event.value)!;
		if (this.isShotTypeSelected(shotType.id, index)) return;
		this.shoots[index].shotTypeId = shotType.id;
		this.shoots[index].shotTypeCode = shotType.code;
		this.shoots[index].shotTypeName = shotType.name;
	}

	selectMediaFormat(event: any, index: number) {
		this.shoots[index].mediaFormatId = event.value;
		this.shoots[index].codifiedUsedId = undefined;
		if(this.shoots[index].aspectRatioId) this.getShotCombinations(index);
	}

	selectAspectRatio(event: any, index: number) {
		this.shoots[index].aspectRatioId = event.value;
		this.shoots[index].codifiedUsedId = undefined;
		if(this.shoots[index].mediaFormatId) this.getShotCombinations(index);
	}

	isCodifiedUsedSelected(id: number, index: number): boolean {
		if (!this.differentShotsForSizeGroups) {
			return this.shoots.some((el) => el.codifiedUsedId === id);
		}

		const type = this.shoots[index];
		if (!!type) return this.shoots.some((el) => el.codifiedUsedId === id && el.sizeGroupId === type?.sizeGroupId);

		return false;
	}

	selectCodifiedUsed(event: any, index: number) {
		const codifiedUsed = this.codifiedUseds.data.find((el) => el.id === event.value)!;
		if (this.isCodifiedUsedSelected(codifiedUsed.id, index)) return;
		this.shoots[index].codifiedUsedId = codifiedUsed.id;
		this.shoots[index].codifiedUsedCode = codifiedUsed.code;
		this.shoots[index].codifiedUsedName = codifiedUsed.description;
	}

	confirm() {
		const template: Template = {
			templateId: this.template?.templateId || 0,
			templateName: this.template?.templateName || '',
			shotsNumber: this.template?.shotsNumber || 0,
			//templateName: undefined,
			brandId: this.brand?.brandId,
			brandName: this.brand?.brandName,
			hasSizeGroup: this.brand?.hasSizeGroup,
			categoryId: this.category?.id,
			// shotsNumber: undefined,
			categoryName: this.category?.name,
			styles: this.stylesSelected,
			colors: this.colorsSelected,
			shotTypes: this.shoots
		};

		this.onSaveTemplate(template);
	}

	cancel() {
		this.#router.navigate(['/company/templates']);
	}

	onBrandChange(event: any) {
		this.shoots = [];
		this.addShot();
		this.getCategories(event);
		this.stylesSelected = [];
		this.colorsSelected = [];
	}

	addSizeGroup() {
		this.shoots.push(
			{
				shotTypeId: undefined,
				shotTypeName: undefined,
				sizeGroupId: this.sizeGroups.selected!.id,
				sizeGroupName: this.sizeGroups.selected!.name,
				sizeIndex: 0
			} as TemplateShotType
		);
		this.sizeGroups.selected = undefined;
		this.setSizeGroupsBooleans();
	}

	private getSizeGroups(brandId: number) {
		this.#graphQLService
			.getData(SharedQueries.GET_SIZE_GROUPS_BY_BRAND, { brandId })
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => res['data']['sizeGroupsByBrand'])
			)
			.subscribe((data: WrapperResultGraphQL<any>) => {
				this.sizeGroups.disabledDeleteAction = false;
				this.sizeGroups.data = UtilityFunctions.DeepClone(data.nodes);
				!this.template?.templateId
					? this.sizeGroups.data.forEach((s) => {
							s.disabled = true;
							this.shoots.push(
								{
									shotTypeId: undefined,
									shotTypeName: undefined,
									sizeGroupId: s.id,
									sizeGroupName: s.name,
									sizeIndex: 0
								} as TemplateShotType
							);
					  })
					: this.setSizeGroupsBooleans();
			});
	}

	private setSizeGroupsBooleans() {
		if (this.brand?.hasSizeGroup && this.differentShotsForSizeGroups) {
			this.sizeGroups.data.forEach((s) => (s.disabled = this.shoots.some((t) => t.sizeGroupId === s.id)));
			this.sizeGroups.disabledDeleteAction = this.sizeGroups.data.filter((s) => s.disabled).length === 1;
		}
	}

	onSaveTemplate(template?: Template) {
		if (template && !template.shotTypes.some((x) => !x.shotTypeCode)) {
			const updateTemplate: UpdateTemplate = {
				templateId: template.templateId || undefined,
				brandId: template.brandId,
				categoryId: template.categoryId || undefined,
				modelIds: template.styles.map((s) => new Ids(s.modelId)),
				colorIds: template.colors.map((s) => new Ids(s.colorId)),
				shotTypes: template.shotTypes.map(
					(type, index) => {
						return {
							shotTypeId: type.shotTypeId || undefined,
							shotTypeName: type.shotTypeName || '',
							shotTypeCode: type.shotTypeCode,
							sizeGroupId: type.sizeGroupId,
							aspectRatioId: type.aspectRatioId,
							mediaFormatId: type.mediaFormatId,
							codifiedUsedId: type.codifiedUsedId,
							ord: index + 1
						} as TemplateShotTypeDTI
					}
				)
			};

			this.#graphQLService
				.postData(MutationQuery.UPDATE_TEMPLATE, { updateTemplate, companyId: this.#appService.companyId, currentUserId: this.#appService.user.userId })
				.pipe(takeUntilDestroyed(this.#destroyRef))
				.subscribe({
					next: () => this.#router.navigate(['/company/templates']),
					error: () => this.#utilitiesService.showSnackbar('Error saving template')
				});
		} else {
			this.#utilitiesService.showSnackbar('Missing fields to save template');
		}
	}

	private getTemplate() {
		this.#graphQLService
			.getData<Template>(TemplatesQueries.GET_TEMPLATE_BY_ID, { templateId: Number(this.#activatedRoute.snapshot.queryParams['templateId']) })
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => res['data']['templateById'])
			)
			.subscribe((data: WrapperResultGraphQL<any>) => {
				this.template = data.nodes[0];
				this.getBrands();
				this.loadInitialData();
				this.shoots = this.template.shotTypes.filter((el) => !!el.shotTypeId) || [];
			});
	}

	private getBrands() {
		this.#graphQLService
			.getData<Brand>(SharedQueries.GET_BRANDS_NO_IDS, { search: '', companyId: Number(this.#appService.companyId) })
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				map((res: any) => res['data']['brands'])
			)
			.subscribe({
				next: (res: WrapperResultGraphQL<any>) => {
					!!this.brands.pageInfo.endCursor
						? (this.brands.data = res.nodes.map((el) => ({ brandId: el.brandId, brandName: el.brand.name, hasSizeGroup: el.hasSizeGroup })))
						: (this.brands.data = this.brands.data.concat(
								res.nodes.map((el) => ({ brandId: el.brandId, brandName: el.brand.name, hasSizeGroup: el.hasSizeGroup }))
						  ));

					this.brands.pageInfo = { ...res.pageInfo };

					if (this.template) {
						this.brand = this.brands.data.find((el) => el.brandId === this.template.brandId);
						this.getCategories({ value: this.brand! });
					}
				}
			});
	}

	private loadInitialData(): void {
		const filter = {
			first: this.shotTypes.size,
			after: this.shotTypes.pageInfo.endCursor,
			search: this.shotTypes.search
		};

		forkJoin({
			shotTypes: this.#graphQLService.getData<ShotType>(TemplatesQueries.GET_SHOT_TYPES, filter).pipe(
				first(),
				map((res: any) => res['data']['shotTypes'])
			),
			mediaTypes: this.#graphQLService.getData<MediaFormat>(TemplatesQueries.GET_MEDIA_FORMATS).pipe(
				first(),
				map((res: any) => res['data']['mediaFormats'])
			),
			aspectRatios: this.#graphQLService.getData(TemplatesQueries.GET_ASPECT_RATIOS).pipe(
				first(),
				map((res: any) => res['data']['aspectRatios'])
			),
			// codifiedUseds: this.#graphQLService
			// 	.getData<CodifiedUsed>(TemplatesQueries.GET_CODIFIED_USEDS, {
			// 		search: this.codifiedUseds.search,
			// 		first: this.codifiedUseds.size,
			// 		after: this.codifiedUseds.pageInfo.endCursor
			// 	})
			// 	.pipe(
			// 		first(),
			// 		map((res: any) => res['data']['codifiedUseds'])
			// 	)
		})
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: (results) => {
					// ShotTypes
					this.shotTypes.data = !this.shotTypes.pageInfo.endCursor
						? UtilityFunctions.DeepClone(results.shotTypes.nodes)
						: this.shotTypes.data.concat(UtilityFunctions.DeepClone(results.shotTypes.nodes));
					this.shotTypes.pageInfo = { ...results.shotTypes.pageInfo };

					// MediaTypes
					this.mediaTypes.data = UtilityFunctions.DeepClone(results.mediaTypes.nodes);

					// AspectRatios
					this.aspectRatios.data = UtilityFunctions.DeepClone(results.aspectRatios.nodes);

					// CodifiedUseds
					// this.codifiedUseds.data = UtilityFunctions.DeepClone(results.codifiedUseds.nodes);

					this.shoots = this.template?.shotTypes.filter((el) => !!el.shotTypeId) || [];
				},
				error: (err) => {
					console.error('Error loading initial data:', err);
				}
			});
	}

	private getCodifiedUseds() {
		const ids = this.shotCombinations.map((el) => el.codifiedUsedId);
		this.#graphQLService.getData<CodifiedUsed>(TemplatesQueries.GET_CODIFIED_USEDS_BY_IDS, {ids}).pipe(
			takeUntilDestroyed(this.#destroyRef),
			map((res: any) => res['data']['codifiedUseds'])
		)
		.subscribe((data: WrapperResultGraphQL<any>) => {
			this.codifiedUseds.data = UtilityFunctions.DeepClone(data.nodes);
		});
	}

	private getShotCombinations(index: number) {
		this.#graphQLService.getData<ShotCombination>(TemplatesQueries.GET_SHOT_COMBINATIONS, { mediaFormatId: this.shoots[index].mediaFormatId!, aspectRatioId: this.shoots[index].aspectRatioId! })
		.pipe(
			takeUntilDestroyed(this.#destroyRef),
			map((res: any) => res['data']['shotCombinations'])
		)
		.subscribe((data: WrapperResultGraphQL<any>) => {
			this.shotCombinations = UtilityFunctions.DeepClone(data.nodes);
			this.getCodifiedUseds();
		});
	}
}
